import * as RDialog from "@radix-ui/react-dialog";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize";
import {
  MobileInvoicePaneTabs,
  INVOICE_PANE_TAB,
  InvoicePaneTab,
} from "./MobileInvoicePaneTabs";
import Loader from "components/DesignSystem/Loader/loader";
import { Button } from "components/DesignSystem/Button/Button";
import { InvoiceActions } from "components/InvoiceAction/InvoiceAction";
import { invoiceInitialValues } from "components/InvoiceForm/InvoiceForm";
import { InvoicePreivew } from "components/InvoicePreivew/InvoicePreivew";
import {
  CLICKED_PREVIEW_AND_SEND_IN_DRAFT_INVOICE_VIEW,
  CLICKED_SEND_INVOICE_FROM_EMAIL_PREVIEW_MODAL,
} from "constants/analyticsEvents";
import { Form, Formik, FormikErrors, setNestedObjectValues } from "formik";
import { useAnalytics } from "hooks/useAnalytics";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useCallback, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  useGenrateShortLinkMutation,
  useGetEntityInvoiceQuery,
  useGetInvoiceSettingsQuery,
  useLazyDownloadInvoiceQuery,
  useSendInvoiceMutation,
  useUpdateInvoiceMutation,
} from "store/apis/invoices";
import { debounce } from "utils/debouncing";
import { AddInvoice } from "./AddInvoice";

import * as Popover from "@radix-ui/react-popover";
import { ConditionalLink } from "components/conditionalLink";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import ToolTip from "components/design/toolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { DownloadInvoice } from "components/DownloadInvoice/DownloadInvoice";
import { ArrowRight } from "components/icons/ArrowRight";
import { ArrowUpRight } from "components/icons/ArrowUpRight";
import { CopyIcon } from "components/icons/CopyIcon";
import { DownloadIcon } from "components/icons/Download";
import { LinkInvoiceToTransactionModal } from "components/InvoiceAction/LinkInvoiceToTransactionModal";
import { InvoiceConfiguration } from "components/InvoiceConfiguration/InvoiceConfiguration";
import { InvoiceUnlinkModal } from "components/InvoiceUnlinkModal/InvoiceUnlinkModal";
import {
  INVOICE_STATUSES,
  INVOICE_STATUSES_OPTIONS,
  INVOICE_STATUSES_TYPE,
  WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP,
} from "constants/invoicing";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { invoiceValidation } from "formValidations/invoiceValidation";
import { AnimatePresence } from "framer-motion";
import { useAppDispatch } from "hooks/useAppDispatch";
import DoubleGreenTick from "static/images/DoubleGreenTick.svg";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BackendError } from "types/utils/error";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import { diffValues } from "utils/diffValues";
import { formatDate } from "utils/formatDate";
import { openLink } from "utils/openLink";
import { InvoiceEditorHeader } from "components/InvoiceForm/InvoiceEditorHeader";
import { Invoice } from "types/Models/invoice";
import { SmallInfo } from "components/icons/SmallInfo";
import {
  invoiceBody,
  invoiceSubject,
  SendInvoiceForm,
} from "components/SendInvoiceModal/SendInvoiceModal";
import { SendInvoice } from "components/SendInvoiceModal/SendInvoice";
import { USD } from "constants/currency";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import { InvoiceSourceSyncBanner } from "components/InvoiceSourceSync/InvoiceSourceSyncBanner";
dayjs.extend(isSameOrAfter);

export const InvoiceStatus = ({
  invoiceStatus,
  pendingAmount,
  invoiceVoidDate,
  invoiceUnCollectibleDate,
  writtenOffAmount,
  writtenOffDate,
  size = "small",
}: {
  size?: "small" | "medium";
  invoiceStatus: INVOICE_STATUSES_TYPE;
  pendingAmount?: number | null;
  invoiceVoidDate?: string | null;
  invoiceUnCollectibleDate?: string | null;
  writtenOffAmount?: number | null;
  writtenOffDate?: string | null;
}) => {
  const isWrittenOff =
    invoiceStatus === INVOICE_STATUSES.PAID && Boolean(writtenOffAmount);

  const tooltipContentMap: Record<INVOICE_STATUSES_TYPE, React.ReactNode> = {
    [INVOICE_STATUSES.PARTIALLY_PAID]: (
      <div className="t-flex t-gap-9">
        <span>Amount pending:</span>
        <span>
          <AmountSuperScript amount={Number(pendingAmount || 0)} />
        </span>
      </div>
    ),
    [INVOICE_STATUSES.VOID]: (
      <span>{`Set to void on ${formatDate(invoiceVoidDate)}`}</span>
    ),
    [INVOICE_STATUSES.UNCOLLECTIBLE]: (
      <span>{`Set to uncollectible on ${formatDate(
        invoiceUnCollectibleDate
      )}`}</span>
    ),
    [INVOICE_STATUSES.DRAFT]: null,
    [INVOICE_STATUSES.PAID]: isWrittenOff ? (
      <span className="t-flex t-gap-1 t-items-center">
        <AmountSuperScript amount={Number(writtenOffAmount)} />
        written off as uncollectible on {formatDate(writtenOffDate)}
      </span>
    ) : null,
    [INVOICE_STATUSES.DUE]: null,
    [INVOICE_STATUSES.OVERDUE]: null,
  };

  const tooltipContent = tooltipContentMap[invoiceStatus];

  const showVoidTooltip =
    invoiceStatus === INVOICE_STATUSES.VOID && invoiceVoidDate;
  const showUncollectibleTooltip =
    invoiceStatus === INVOICE_STATUSES.UNCOLLECTIBLE &&
    invoiceUnCollectibleDate;

  return (
    <ConditionalToolTip condition={tooltipContent}>
      <span>
        <Tag
          rounded
          size={size}
          icon={false}
          tagType={
            {
              [INVOICE_STATUSES.DRAFT]: "gray",
              [INVOICE_STATUSES.PAID]: "green",
              [INVOICE_STATUSES.PARTIALLY_PAID]: "yellow",
              [INVOICE_STATUSES.VOID]: "transparent",
              [INVOICE_STATUSES.UNCOLLECTIBLE]: "gray",
              [INVOICE_STATUSES.DUE]: "yellow",
              [INVOICE_STATUSES.OVERDUE]: "red",
            }[invoiceStatus] as "gray" | "purple" | "green" | "yellow" | "red"
          }
        >
          <div className="t-flex t-items-center">
            {INVOICE_STATUSES_OPTIONS.find((opt) => opt.value === invoiceStatus)
              ?.label || capitalizeFirstLetter(invoiceStatus.toLowerCase())}
            {(showUncollectibleTooltip || showVoidTooltip || isWrittenOff) && (
              <span className="t-flex t-items-center t-align-middle t-ml-1">
                <SmallInfo />
              </span>
            )}
          </div>
        </Tag>
      </span>
    </ConditionalToolTip>
  );
};

const InvoiceShareDropDown = ({
  invoice,
  from,
}: {
  invoice: Invoice;
  from: "mail_draft_screen" | "invoice_edit_screen";
}) => {
  const entityId = useCurrentEntityId();
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast, successToast } = useToast();

  const [getInvoiceDownloadUrl] = useLazyDownloadInvoiceQuery();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const [generateLinkToken] = useGenrateShortLinkMutation();

  const downloadInvoice = async () => {
    try {
      const { doc_id } = await getInvoiceDownloadUrl({
        groupId: groupId,
        entityId,
        invoiceId: invoice.uuid,
      }).unwrap();

      const { download_url } = await getPreviewUrl({
        groupId: groupId!,
        fileId: doc_id,
      }).unwrap();

      openLink(download_url, "_blank");
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
    if (invoice.source !== REVENUE_DATA_SOURCE.MANUAL) {
      try {
        if (groupId && entityId) {
          const { download_url } = await getPreviewUrl({
            groupId: groupId!,
            fileId: invoice?.document?.file_id!,
          }).unwrap();
          openLink(download_url, "_blank");
        }
      } catch (error) {
        alertToast({ message: (error as BackendError)?.data?.error?.message });
      }
    }
  };

  const copyInvoiceLink = async () => {
    try {
      const { token } = await generateLinkToken({
        entityId,
        invoiceId: invoice.uuid,
        from,
      }).unwrap();

      const origin = window.location.origin;
      await navigator.clipboard.writeText(`${origin}/invoice/${token}`);
      successToast({ message: "Invoice link has been copied" });
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <div className="t-group">
          <Button size="small">
            Share
            <div className="t-rotate-90 group-data-state-open:-t-rotate-90">
              <ArrowRight color="currentColor" />
            </div>
          </Button>
        </div>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content>
          <Dropdown.Item onSelect={downloadInvoice}>
            <DownloadIcon />
            Download invoice
          </Dropdown.Item>
          <Dropdown.Item onSelect={copyInvoiceLink}>
            <CopyIcon className="t-text-text-30" />
            Copy invoice link
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
};

const SendInvoiceButton = ({
  invoice,
  submit,
  sending,
}: {
  invoice: Invoice;
  submit: () => void;
  sending: boolean;
}) => {
  const [open, setOpen] = useState(false);

  const onConfirm = () => {
    setOpen(false);
    submit();
  };

  const onCancel = () => {
    setOpen(false);
  };

  const isFutureDate = dayjs(invoice.invoice_date).isSameOrAfter(
    dayjs().startOf("day")
  );

  if (isFutureDate && invoice.currency !== USD) {
    return (
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <Button
            type="button"
            size="small"
            customType="primary"
            isLoading={sending}
            disabled={sending}
          >
            Send
          </Button>
        </Popover.Trigger>
        <Popover.Content className="t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-z-tooltip t-text-center t-break-all t-hyphen-auto t-shadow-dark-30">
          <Popover.Arrow />
          <div className="t-flex t-flex-col t-gap-4 t-text-left t-w-[264px]">
            <div className="t-flex t-flex-col t-gap-2">
              <p className="t-m-0 t-text-subtitle-sm">
                Future Invoice Date Selected
              </p>
              <p className="t-m-0 t-text-body-sm t-break-keep">
                The invoice date you've selected is in the future. As a result,
                the exchange rate applied is based on today's rate.
                <br />
                <br />
                Please note that this may lead to differences during
                reconciliation, especially if the actual exchange rate on the
                invoice date varies.
                <br />
                <br />
                Are you sure?
              </p>
            </div>
            <div className="t-flex t-gap-2 t-w-full t-justify-end">
              <Button type="button" size="small" block onClick={onConfirm}>
                Confirm
              </Button>
            </div>
          </div>
        </Popover.Content>
      </Popover.Root>
    );
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          size="small"
          customType="primary"
          isLoading={sending}
          disabled={
            sending ||
            ((invoice.customer?.recipient_emails?.length ?? 0) <= 0 &&
              !invoice?.customer?.email) ||
            invoice.invoice_items.length <= 0
          }
        >
          Send
        </Button>
      </Popover.Trigger>
      <Popover.Content className="t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-z-tooltip t-text-center  t-hyphen-auto t-shadow-dark-30">
        <Popover.Arrow />
        <div className="t-flex t-flex-col t-gap-4 t-text-left t-w-[264px]">
          <div className="t-flex t-flex-col t-gap-2">
            <p className="t-m-0 t-text-subtitle-sm">Send invoice?</p>
            <p className="t-m-0 t-text-body-sm t-break-keep">
              This will email the invoice to the customer.
            </p>
          </div>
          <div className="t-grid t-grid-cols-2 t-gap-2 t-w-full">
            <Button type="button" size="small" block onClick={onCancel}>
              Cancel
            </Button>
            <Button type="button" size="small" block onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

const Content = ({ close }: { close?: () => void }) => {
  const { invoiceId } = useParams<{
    invoiceId: string;
  }>();
  const entityId = useCurrentEntityId();
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const query = useQuery();
  const isSendInvoiceOpen = Boolean(query.get("is_send_invoice_open"));
  const { alertToast, successToast } = useToast();
  const sendInvoiceStep = useModal(isSendInvoiceOpen);
  const { trackEvent } = useAnalytics();
  const editFlow = query.get("editflow");
  const previewFromEdit = query.get("from_edit");
  const confirmChangesModal = useModal();
  const { updateMultiple } = useUpdateQuery();
  const history = useHistory();
  const { link } = useConstructInternalLink();

  const linkExistingTransactionModal = useModal();
  const [sendInvoice, { isLoading: sendingInvoice }] = useSendInvoiceMutation();
  const entity = entities.find((entity) => entity.uuid === entityId);

  const {
    data: invoice,
    isLoading: invoiceLoading,
    isFetching,
  } = useGetEntityInvoiceQuery(
    {
      groupId,
      entityId,
      invoiceId,
    },
    { skip: !groupId || !entityId || !invoiceId }
  );

  const { data: invoiceSetting } = useGetInvoiceSettingsQuery(
    {
      groupId,
      entityId,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const isInvoiceSent =
    invoice && (invoice.status === "DUE" || invoice.status === "OVERDUE");
  const isSentInvoiceEditFlow =
    hasWritePermission &&
    (invoice?.status === "DUE" || invoice?.status === "OVERDUE") &&
    Boolean(editFlow);
  const [updateInvoice, { isLoading: isUpdating, isSuccess }] =
    useUpdateInvoiceMutation();
  const prevInvoiceValuesRef = useRef<Partial<Invoice> | null>(null);

  const lockedFinancialEdit = useModal();

  const onHandleChange = useCallback(
    async (
      values: Partial<Invoice>,
      setErrors: (errors: FormikErrors<Invoice>) => void,
      errors: FormikErrors<Invoice>,
      setFieldValue: (field: string, value: any) => void
    ) => {
      if (isSentInvoiceEditFlow) return;

      if (prevInvoiceValuesRef.current === null) {
        prevInvoiceValuesRef.current = values;
        return;
      }

      if (groupId && entityId) {
        const { invoice_items: _items, ...payload } = diffValues(
          prevInvoiceValuesRef.current,
          values
        );

        if (Object.keys(payload).length === 0) {
          return;
        }

        if (Object.keys(payload).length === 1 && "invoice_items" in payload) {
          prevInvoiceValuesRef.current = values;
          return;
        }

        try {
          const response = await updateInvoice({
            payload,
            groupId: groupId,
            entityId: entityId,
            invoiceId,
          }).unwrap();
          const { field_errors } = response || {};

          if (field_errors) {
            setErrors(field_errors);
          }

          if (response?.invoice_number) {
            setFieldValue("invoice_number", response.invoice_number);
          }

          prevInvoiceValuesRef.current = {
            ...values,
            invoice_number: response?.invoice_number || values.invoice_number,
          };
        } catch (error) {
          alertToast(
            { message: (error as BackendError).data?.error?.message },
            error as Error
          );
        }
      }
    },
    [isSentInvoiceEditFlow, entityId, groupId, invoiceId]
  );

  const onChange = useCallback(debounce(onHandleChange, 300), [onHandleChange]);

  const closeSendInvoiceModal = () => {
    sendInvoiceStep.close();
    updateMultiple([
      {
        query: "is_send_invoice_open",
        value: null,
      },
      ...(previewFromEdit
        ? [
            {
              query: "from_edit",
              value: null,
            },
            { query: "editflow", value: true },
          ]
        : []),
    ]);
  };

  const cancelChanges = () => {
    confirmChangesModal.close();
    updateMultiple([
      {
        query: "editflow",
        value: null,
      },
      {
        query: "from_edit",
        value: true,
      },
    ]);
  };

  const handleMarkAsActive = async () => {
    if (!groupId || !entityId || !invoiceId) return;

    try {
      await updateInvoice({
        payload: { status: INVOICE_STATUSES.DUE },
        groupId,
        entityId,
        invoiceId,
      }).unwrap();
      successToast({
        message: "Invoice marked as active",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onPreviewOrSend = async () => {
    trackEvent(CLICKED_PREVIEW_AND_SEND_IN_DRAFT_INVOICE_VIEW);
    const isValid = invoiceValidation.isValidSync(invoice);

    if (!isValid) {
      return alertToast({
        message: "Please fill all the required fields.",
      });
    }
    if (isSentInvoiceEditFlow) {
      confirmChangesModal.open();
      return;
    }
    sendInvoiceStep.open();
    setActivePane(INVOICE_PANE_TAB.EDIT);
    cancelChanges();
  };

  const unLinkTransactionModal = useModal();
  const [activePane, setActivePane] = useState<InvoicePaneTab>(
    INVOICE_PANE_TAB.EDIT
  );
  const { isMobile } = useWindowSize();
  const isEditPaneHidden = activePane !== INVOICE_PANE_TAB.EDIT;
  const isPreviewPaneHidden = activePane !== INVOICE_PANE_TAB.PREVIEW;
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >(undefined);

  if (invoiceLoading) {
    return (
      <div className="t-flex t-h-full t-w-full t-justify-center t-items-center t-col-span-2">
        <Loader />
      </div>
    );
  }

  if (!invoice || !entity || invoiceLoading || !invoiceSetting) {
    return null;
  }

  const onBackClick = () => {
    if (sendInvoiceStep.isOpen) {
      closeSendInvoiceModal();
      return;
    }

    if (close) {
      close();
    } else {
      history.goBack();
    }
  };

  const onSendInvoice = async (values: {
    attachments: File[];
    body: string;
    subject: string;
    cc_emails: string[];
    from_email_type: string;
    recipients: string[];
  }) => {
    trackEvent(CLICKED_SEND_INVOICE_FROM_EMAIL_PREVIEW_MODAL);
    try {
      await sendInvoice({
        groupId: groupId,
        entityId: entityId,
        invoiceId: invoiceId,
        payload: {
          subject: values.subject,
          body: values.body,
          attachments: values.attachments,
          cc_emails: values.cc_emails?.join(",") || "",
          from_email_type: values.from_email_type,
          recipients: values.recipients?.join(",") || "",
        },
      }).unwrap();
      successToast({ message: "Invoice has been sent." });
      const sendPageUrl = link(
        `/books/invoices-and-customers/${invoiceId}/send`
      );
      history.replace(sendPageUrl);
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message }, error as Error);
    }
  };

  const updateChanges = async (values: Omit<Invoice, "status" | "uuid">) => {
    try {
      await updateInvoice({
        payload: values as Partial<Invoice>,
        groupId: groupId,
        entityId: entityId,
        invoiceId,
      }).unwrap();
      successToast({ title: "Successfully updated" });
      sendInvoiceStep.open();
      setActivePane(INVOICE_PANE_TAB.EDIT);
      cancelChanges();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const isReminder =
    !isSentInvoiceEditFlow &&
    (invoice.status === "DUE" || invoice.status === "OVERDUE");

  const defaultBody = invoiceBody({
    entity,
    invoice,
    invoiceSetting,
    isSentInvoiceEditFlow,
    currencyCode: invoice?.currency,
  });

  const subject = invoiceSubject({
    entity,
    invoice,
    isSentInvoiceEditFlow,
    isReminder,
  });

  const isInvoiceWrittenOff = Boolean(invoice.written_off_amount);

  const linkedTransactionsLabel = `View linked${
    invoice.associated_txns?.length === 1 ? " transaction" : " transactions"
  } (${invoice.associated_txns?.length})`;

  let linkedTransactionsTrigger = null;
  if (invoice.associated_txns?.length && isMobile) {
    linkedTransactionsTrigger = (
      <span className="t-cursor-pointer t-text-body-sm t-font-medium t-text-purple">
        {linkedTransactionsLabel}
      </span>
    );
  }
  if (invoice.associated_txns?.length && !isMobile) {
    linkedTransactionsTrigger = (
      <ConditionalLink
        target="_blank"
        to={`/books/transactions/?entity=${invoiceSetting?.entity_id}&company=${groupId}&transaction_id=${invoice.associated_txns[0].uuid}`}
      >
        <Button customType="link" size="small" type="button">
          {linkedTransactionsLabel}
        </Button>
      </ConditionalLink>
    );
  }

  const invoiceHeader = (
    <InvoiceEditorHeader
      status={
        <InvoiceStatus
          size="medium"
          invoiceStatus={invoice.status}
          pendingAmount={invoice.pending_amount}
          writtenOffAmount={invoice.written_off_amount}
          writtenOffDate={invoice.written_off_at}
        />
      }
      title={invoice.invoice_number}
      onBackClick={isUpdating ? () => null : onBackClick}
      right={
        <>
          {invoice.associated_txns && invoice.associated_txns.length > 0 && (
            <ToolTip
              text={
                <div
                  className="t-min-w-64 t-text-left t-flex t-flex-col t-gap-0.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  {invoice.associated_txns.map((txn) => (
                    <div className="t-flex t-justify-between" key={txn.uuid}>
                      <div className="t-flex t-justify-between t-items-center">
                        <div>{formatDate(txn.date)}</div>
                      </div>
                      <ConditionalLink
                        target="_blank"
                        to={`/books/transactions/?entity=${invoiceSetting?.entity_id}&company=${groupId}&transaction_id=${txn.uuid}`}
                      >
                        <Button customType="link" size="small" type="button">
                          <span className="t-text-white t-underline t-ml-2">
                            <AmountSuperScript amount={Number(txn.amount)} />
                          </span>
                        </Button>
                      </ConditionalLink>
                      {/* Each Transaction UnLink */}
                      <div className="t-ml-auto t-flex t-items-center t-gap-3">
                        <ConditionalLink
                          target="_blank"
                          to={`/books/transactions/?entity=${invoiceSetting?.entity_id}&company=${groupId}&transaction_id=${txn.uuid}`}
                        >
                          <span className="t-text-purple-40">View</span>
                        </ConditionalLink>
                        <ConditionalToolTip
                          condition={
                            isInvoiceWrittenOff &&
                            WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP
                          }
                        >
                          <span>
                            <button
                              className="all:unset t-text-purple-40 disabled:t-text-neutral-30 disabled:t-cursor-not-allowed"
                              disabled={isInvoiceWrittenOff}
                              onClick={() => {
                                setSelectedTransactionId(txn.uuid);
                                unLinkTransactionModal.open();
                              }}
                            >
                              Unlink
                            </button>
                          </span>
                        </ConditionalToolTip>
                      </div>
                    </div>
                  ))}
                </div>
              }
            >
              <span>{linkedTransactionsTrigger}</span>
            </ToolTip>
          )}
        </>
      }
    />
  );

  if (sendInvoiceStep.isOpen) {
    return (
      <>
        <Formik<SendInvoiceForm>
          key="SendInvoiceForm"
          onSubmit={onSendInvoice}
          initialValues={{
            attachments: [] as File[],
            subject: subject,
            body: defaultBody,
            attachWform: invoice?.should_send_w_form,
            wform: invoice?.w_form || null,
            from_email_type: "INKLE",
            cc_emails: [],
            recipients:
              invoice?.customer?.recipient_emails ||
              (invoice?.customer?.email ? [invoice.customer.email] : []),
          }}
        >
          {({ submitForm }) => {
            const savedStatus = (
              <Async.Root
                isLoading={isUpdating || isFetching}
                customLoader={<Loader customType="secondary" />}
                isEmpty={
                  !invoice || invoice?.created_on === invoice?.updated_at
                }
                isSuccess={Boolean(invoice)}
              >
                <Async.Empty>
                  <></>
                </Async.Empty>
                <Async.Success>
                  <span className="t-flex t-items-center t-gap-2 t-transition-all">
                    <img src={DoubleGreenTick} alt="Saved" />
                    {`Saved ${dayjs(invoice?.updated_at).fromNow()}`}
                  </span>
                </Async.Success>
              </Async.Root>
            );

            const mobileTabsStatus = (
              <span className="t-text-body-sm t-text-text-30">
                {savedStatus}
              </span>
            );

            const actionRow = (
              <div className="t-flex t-gap-2 t-bg-surface-lighter-grey t-items-center sm:t-bg-surface sm:t-justify-end sm:t-w-full">
                {invoice.ledger_entry?.uuid && (
                  <ConditionalLink
                    target="_blank"
                    to={link("/books/journal-entries", {
                      moreQuery: {
                        journal_entry_id: invoice.ledger_entry.uuid,
                      },
                    })}
                  >
                    <Button customType="link" size="small" type="button">
                      View journal entry
                    </Button>
                  </ConditionalLink>
                )}
                <span className="t-text-body-sm t-text-text-30 t-ml-auto sm:t-hidden">
                  {savedStatus}
                </span>
                {invoice &&
                  (invoice.status === "DRAFT" || isSentInvoiceEditFlow) && (
                    <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                      <Button
                        customType="secondary"
                        size="small"
                        onClick={handleMarkAsActive}
                        isLoading={isUpdating}
                        disabled={
                          isUpdating ||
                          !invoice.customer_id ||
                          !invoice.invoice_date ||
                          invoice.invoice_items.length <= 0
                        }
                      >
                        Mark as active
                      </Button>
                    </LockedUi>
                  )}

                {invoice.status !== "DRAFT" && (
                  <InvoiceShareDropDown
                    invoice={invoice}
                    from="mail_draft_screen"
                  />
                )}

                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.REVENUE}
                  blockedUi={<LockedButton text="Send" />}
                >
                  <SendInvoiceButton
                    invoice={invoice}
                    submit={submitForm}
                    sending={sendingInvoice}
                  />
                </LockedUi>

                {invoice && !isSentInvoiceEditFlow && (
                  <InvoiceActions invoice={invoice} isFromInvoiceDetails />
                )}
              </div>
            );

            return (
              <>
                <div
                  className={classNames(
                    "t-overflow-auto t-pt-6 t-shadow-light-100",
                    {
                      "sm:t-hidden": isEditPaneHidden,
                    }
                  )}
                >
                  <DashboardContainer className="t-h-full sm:t-h-auto">
                    <DashboardContainer.Header className="t-px-8 t-pb-4 t-bg-surface t-z-header sm:t-px-4">
                      {invoiceHeader}
                      {isMobile && (
                        <div className="t-block">
                          <MobileInvoicePaneTabs
                            value={activePane}
                            onChange={setActivePane}
                            editLabel="Email"
                            right={mobileTabsStatus}
                          />
                        </div>
                      )}
                    </DashboardContainer.Header>
                    <DashboardContainer.Content className="t-pb-6 sm:t-pb-24 sm:t-overflow-visible">
                      <div className="t-px-8 t-pt-6 sm:t-px-4 ">
                        <AnimatePresence>
                          <SendInvoice invoice={invoice} />
                        </AnimatePresence>
                      </div>
                    </DashboardContainer.Content>
                  </DashboardContainer>
                </div>
                <div
                  className={classNames(
                    "t-overflow-auto t-pt-6 t-bg-surface-lighter-grey",
                    { "sm:t-hidden": isPreviewPaneHidden }
                  )}
                >
                  <DashboardContainer className="t-h-full sm:t-h-auto">
                    <DashboardContainer.Header className="t-px-8 t-pb-5 t-z-header sm:t-px-4">
                      {isMobile && (
                        <div className="t-block t-bg-surface">
                          {invoiceHeader}
                          <MobileInvoicePaneTabs
                            value={activePane}
                            onChange={setActivePane}
                            editLabel="Email"
                            right={mobileTabsStatus}
                          />
                        </div>
                      )}
                      {!isMobile && actionRow}
                    </DashboardContainer.Header>
                    <DashboardContainer.Content className="t-pb-6 sm:t-pb-24 sm:t-overflow-visible">
                      <div className="t-px-8 sm:t-px-4">
                        <InvoicePreivew invoice={invoice} />
                      </div>
                    </DashboardContainer.Content>
                  </DashboardContainer>
                </div>
                {isMobile && (
                  <div className="t-fixed t-bottom-0 t-left-0 t-right-0 t-z-header t-bg-surface t-px-3 t-py-3 t-shadow-light-100">
                    {actionRow}
                  </div>
                )}
              </>
            );
          }}
        </Formik>

        <LinkInvoiceToTransactionModal
          invoiceId={invoiceId}
          isOpen={linkExistingTransactionModal.isOpen}
          close={linkExistingTransactionModal.close}
        />

        {invoice && (
          <InvoiceUnlinkModal
            invoiceId={invoice.uuid}
            isOpen={unLinkTransactionModal.isOpen}
            invoiceType="INVOICE"
            close={() => {
              setSelectedTransactionId(undefined);
              unLinkTransactionModal.close();
            }}
            transactionId={selectedTransactionId}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Formik
        key="EditInvoiceForm"
        onSubmit={updateChanges}
        validateOnBlur
        validateOnChange={true}
        validationSchema={invoiceValidation}
        initialValues={
          invoice
            ? { ...invoice, status: undefined }
            : (invoiceInitialValues as Omit<Invoice, "uuid" | "document">)
        }
      >
        {({
          submitForm: updateInvoiceChanges,
          setErrors,
          validateForm,
          setTouched,
          errors,
          values,
          setFieldValue,
        }) => {
          const invoiceItems = values.invoice_items.map((item) => ({
            ...item,
            amount: values.has_item_level_tax
              ? String(item.amount || 0)
              : String((item.rate || 0) * (item.quantity || 0)),
          }));

          const subtotal =
            invoiceItems?.reduce((acc, item) => acc + Number(item.amount), 0) ||
            0;

          let tax = 0;

          let discount = 0;

          if (values.discount_type === "PERCENT") {
            discount = subtotal * ((Number(values.discount) || 0) / 100);
          }

          if (values.discount_type === "ABSOLUTE") {
            discount = Number(values.discount);
          }

          if (values.tax_type === "PERCENT") {
            tax = (subtotal - discount) * ((Number(values.tax) || 0) / 100);
          }

          if (values.tax_type === "ABSOLUTE") {
            tax = Number(values.tax);
          }

          const total = subtotal - discount + tax;
          const due = total - Number(values.paid_amount || 0);

          const savedStatus = (
            <>
              {!isSentInvoiceEditFlow && (
                <Async.Root
                  isLoading={isUpdating || isFetching}
                  customLoader={<Loader customType="secondary" />}
                  isEmpty={
                    !invoice || invoice?.created_on === invoice?.updated_at
                  }
                  isSuccess={Boolean(invoice)}
                >
                  <Async.Empty>
                    <></>
                  </Async.Empty>
                  <Async.Success>
                    <span className="t-flex t-items-center t-gap-2 t-transition-all">
                      <img src={DoubleGreenTick} alt="Saved" />
                      {`Saved ${dayjs(invoice?.updated_at).fromNow()}`}
                    </span>
                  </Async.Success>
                </Async.Root>
              )}
            </>
          );

          const mobileTabsStatus = (
            <span className="t-text-body-sm t-text-text-30">{savedStatus}</span>
          );

          const actionRow = (
            <div className="t-flex t-gap-2 t-bg-surface-lighter-grey t-items-center sm:t-bg-surface sm:t-justify-end sm:t-w-full">
              {invoice.ledger_entry?.uuid && (
                <ConditionalLink
                  target="_blank"
                  to={link("/books/journal-entries", {
                    moreQuery: {
                      journal_entry_id: invoice.ledger_entry.uuid,
                    },
                  })}
                >
                  <Button customType="link" size="small" type="button">
                    View journal entry
                  </Button>
                </ConditionalLink>
              )}
              <span className="t-text-body-sm t-text-text-30 t-ml-auto sm:t-hidden">
                {savedStatus}
              </span>
              {invoice && invoice.status === "DRAFT" && (
                <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={handleMarkAsActive}
                    isLoading={isUpdating}
                    disabled={
                      isUpdating ||
                      !invoice.customer_id ||
                      !invoice.invoice_date ||
                      invoice.invoice_items.length <= 0
                    }
                  >
                    Mark as active
                  </Button>
                </LockedUi>
              )}

              {invoice.status !== "DRAFT" &&
                invoice.status !== "OVERDUE" &&
                invoice.status !== "DUE" && (
                  <InvoiceShareDropDown
                    invoice={invoice}
                    from="invoice_edit_screen"
                  />
                )}

              <LockedUi
                feature={WRITE_PERMISSION_KEYS.REVENUE}
                blockedUi={
                  [
                    INVOICE_STATUSES.DUE.toString(),
                    INVOICE_STATUSES.OVERDUE.toString(),
                    INVOICE_STATUSES.DRAFT.toString(),
                  ].includes(invoice.status) && (
                    <LockedButton
                      text={isInvoiceSent ? "Remind" : "Draft email & send"}
                    />
                  )
                }
              >
                <Popover.Root>
                  <Popover.Trigger asChild>
                    {[
                      INVOICE_STATUSES.DUE.toString(),
                      INVOICE_STATUSES.OVERDUE.toString(),
                      INVOICE_STATUSES.DRAFT.toString(),
                    ].includes(invoice.status) && (
                      <Button
                        type="button"
                        size="small"
                        customType="primary"
                        disabled={
                          !invoice.customer_id ||
                          !invoice.invoice_date ||
                          (!isSentInvoiceEditFlow &&
                            invoice.invoice_items.length <= 0)
                        }
                        onClick={
                          isSentInvoiceEditFlow
                            ? () => {}
                            : async () => {
                                const errors = await validateForm();
                                if (Object.keys(errors).length > 0) {
                                  setTouched(
                                    setNestedObjectValues(errors, true)
                                  );
                                }
                                onPreviewOrSend();
                              }
                        }
                      >
                        {isSentInvoiceEditFlow
                          ? "Save & Preview"
                          : isInvoiceSent
                          ? "Remind"
                          : "Draft email & send"}
                      </Button>
                    )}
                  </Popover.Trigger>
                  {isSentInvoiceEditFlow && (
                    <Popover.Content className="t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-z-tooltip t-text-center t-break-all t-hyphen-auto t-shadow-dark-30">
                      <Popover.Arrow />
                      <div className="t-flex t-flex-col t-gap-4 t-text-left t-w-[264px]">
                        <div className="t-flex t-flex-col t-gap-2">
                          <p className="t-m-0 t-text-subtitle-sm">
                            Confirm Changes
                          </p>
                          <p className="t-m-0 t-text-body-sm t-break-keep">
                            Invoice will be updated. This can't be reversed.
                          </p>
                        </div>
                        <div className="t-grid t-grid-cols-2 t-gap-2 t-w-full">
                          <Button
                            type="button"
                            size="small"
                            block
                            onClick={cancelChanges}
                            disabled={isUpdating}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="small"
                            block
                            onClick={updateInvoiceChanges}
                            isLoading={isUpdating}
                          >
                            Confirm
                          </Button>
                        </div>
                      </div>
                    </Popover.Content>
                  )}
                </Popover.Root>
              </LockedUi>
              {invoice && !isSentInvoiceEditFlow && (
                <InvoiceActions invoice={invoice} isFromInvoiceDetails />
              )}
            </div>
          );

          return (
            <>
              <Form
                className={classNames("t-overflow-auto", {
                  "sm:t-hidden": isEditPaneHidden,
                })}
              >
                <div className="t-sticky t-top-0 t-z-header t-bg-surface t-px-8 t-pt-6 t-pb-4 sm:t-px-4">
                  {invoiceHeader}
                  {isMobile && (
                    <div className="t-block">
                      <MobileInvoicePaneTabs
                        value={activePane}
                        onChange={setActivePane}
                        right={mobileTabsStatus}
                      />
                    </div>
                  )}
                </div>
                <div className="t-px-8 t-pt-8 t-pb-6 t-flex t-flex-col t-gap-4 sm:t-px-4 sm:t-pt-3 sm:t-pb-24">
                  {isMobile &&
                    invoice.source === REVENUE_DATA_SOURCE.MANUAL && (
                      <InvoiceConfiguration />
                    )}
                  <InvoiceSourceSyncBanner invoice={invoice} />
                  <AnimatePresence>
                    <AddInvoice
                      onChange={(values) =>
                        onChange(values, setErrors, errors, setFieldValue)
                      }
                      readOnly={!hasWritePermission}
                    />
                  </AnimatePresence>
                </div>
              </Form>
              <Form
                className={classNames(
                  "t-overflow-auto t-pt-6 t-bg-surface-lighter-grey",
                  { "sm:t-hidden": isPreviewPaneHidden }
                )}
              >
                <DashboardContainer className="t-h-full sm:t-h-auto">
                  <DashboardContainer.Header className="t-px-8 t-z-header sm:t-px-4">
                    {isMobile && (
                      <div className="t-block t-bg-surface">
                        {invoiceHeader}
                        <MobileInvoicePaneTabs
                          value={activePane}
                          onChange={setActivePane}
                          right={mobileTabsStatus}
                        />
                      </div>
                    )}
                    {!isMobile && actionRow}
                    <div className="t-flex t-items-start t-justify-between t-gap-4 t-pt-3.5 t-pb-3 t-bg-surface-lighter-grey">
                      <span className="t-text-subtext-lg t-text-text-100 sm:t-whitespace-nowrap">
                        Invoice preview
                      </span>
                      {invoice.source === REVENUE_DATA_SOURCE.MANUAL &&
                        !isMobile && <InvoiceConfiguration />}
                    </div>
                  </DashboardContainer.Header>
                  <DashboardContainer.Content className="t-pb-6 sm:t-pb-24 sm:t-overflow-visible">
                    <div className="t-px-8 t-h-full t-w-full sm:t-px-4">
                      <div className="t-flex t-flex-col t-h-full">
                        <InvoicePreivew
                          fileId={
                            invoice.source !== REVENUE_DATA_SOURCE.MANUAL
                              ? invoice.document?.file_id
                              : ""
                          }
                          invoice={{
                            ...values,
                            sub_total: String(subtotal),
                            total: String(total),
                            tax_amount: String(tax),
                            discount_amount: String(discount),
                            due_balance: String(due),
                            invoice_items: invoiceItems,
                          }}
                        />
                      </div>
                    </div>
                  </DashboardContainer.Content>
                </DashboardContainer>
              </Form>
              {isMobile && (
                <div className="t-fixed t-bottom-0 t-left-0 t-right-0 t-z-header t-bg-surface t-px-4 t-py-3 t-shadow-light-100">
                  {actionRow}
                </div>
              )}
            </>
          );
        }}
      </Formik>

      <LinkInvoiceToTransactionModal
        invoiceId={invoiceId}
        isOpen={linkExistingTransactionModal.isOpen}
        close={linkExistingTransactionModal.close}
      />

      <Modal.Root open={lockedFinancialEdit.isOpen} onOpenChange={close}>
        <Modal.Content size="small">
          <Modal.Header>
            <Modal.Title>You're editing locked financials</Modal.Title>
            <Modal.Close onClick={lockedFinancialEdit.close} />
          </Modal.Header>

          <Modal.Body className="t-text-body-sm t-text-text-60">
            <span className="t-font-bold">
              This entry is from a locked period and therefore cannot be edited.
            </span>
            <br />
            To edit it, you'll need to unlock the period in settings, as changes
            could affect finalized books and reports.
            <br />
            <br />
            Do you want to backdate and unlock the period to continue?
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Button onClick={lockedFinancialEdit.close}>Cancel</Button>

            <ConditionalLink to="/books/books-settings">
              <Button customType="primary">
                <div className="t-flex t-gap-2 t-items-center">
                  <span>Go to settings</span>
                  <span>
                    <ArrowUpRight />
                  </span>
                </div>
              </Button>
            </ConditionalLink>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {invoice && (
        <InvoiceUnlinkModal
          invoiceId={invoice.uuid}
          isOpen={unLinkTransactionModal.isOpen}
          invoiceType="INVOICE"
          close={() => {
            setSelectedTransactionId(undefined);
            unLinkTransactionModal.close();
          }}
          transactionId={selectedTransactionId}
        />
      )}
    </>
  );
};

export const InvoiceFullPageView = ({ close }: { close?: () => void }) => {
  return (
    <RDialog.Root open={true} modal={false}>
      <RDialog.Portal>
        <RDialog.Content className="t-fixed t-z-modal t-top-[50%] t-left-[50%] t-translate-x-[-50%] t-translate-y-[-50%] t-w-full t-h-full t-bg-surface t-grid t-grid-cols-2 sm:t-grid-cols-1">
          <Content close={close} />
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
};
